import React from 'react';
import { Tooltip, Typography } from 'antd';
import { INGREDIENTS } from 'constants/Scope3Constants/category1.constants';
import { FieldType } from 'enums/FieldTypeEnums';
import { Category1TypeFields, Category1Types } from 'enums/category1Enums';
import { CategoryOptions } from 'shared/components/CreateProductModal/ProductModalForm';
import { FieldsRowProps } from 'shared/components/FieldsRow';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import styles from './farmCreationFlow.module.scss';
import { IndexBadge } from 'shared/components/IndexBadge';
import {
  UseFieldArrayRemove,
  UseFormSetValue,
  useFormContext,
} from 'react-hook-form';
import { ReactComponent as MapSearch } from 'assets/icons/map-search.svg';
import { ReactComponent as Add } from 'assets/icons/add.svg';
import { FieldProps } from 'enums/RawMaterialEnums';
import { DefaultOptionType } from 'antd/lib/select';
import { commodityQuantityUnits } from 'constants/Scope3Constants/category4.constants';
import { allowOnlyNumbersUptoN } from 'shared/utils/convertFormData';
import {
  categoryTypeFieldMap,
  getFieldName,
} from 'views/Scope3/Category4&9/Category4Layout/category4LayoutData';
import { FarmboxRawMaterialsData } from 'models/FarmboxRawMaterials/farmBoxRawMaterials.model';
import clsx from 'clsx';
import { BestFitRegion } from 'models/BestFitRegion/bestFitRegion.model';

export interface RawMaterialsLayoutDataProps {
  index: number;
  type: Category1Types;
  nameOptions?: CategoryOptions[];
  bestFitRegionOptions?: BestFitRegion[];
  originsLength?: number;
  ingredientsLength?: number;
  remove?: UseFieldArrayRemove;
  originIndex?: number;
  appendOrigin?: () => void;
  removeOrigin?: (index?: number) => void;
  setIsAddIngredient?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddRegion?: React.Dispatch<React.SetStateAction<boolean>>;
  setValue?: UseFormSetValue<FarmboxRawMaterialsData>;
  handleChange?: () => void;
  setActiveTabKey?: React.Dispatch<React.SetStateAction<string | string[]>>;
  loading?: boolean;
  fetchData: (params?: { 'input-field': string }) => void;
  isDisabled?: boolean;
  handleOriginShare?: () => void;
}
const { LABELS, GUTTER_VALUE, ADD_ORIGIN } = INGREDIENTS;

const {
  FERTILISER_ORIGIN,
  PACKAGING,
  FERTILISER,
  PACKAGING_ORIGIN,
  BEST_FIT_REGION,
  QUANTITY,
  UNIT,
} = LABELS;
const { PACKAGING_MATERIALS } = Category1Types;
const { REQUEST_FERTILISER, REQUEST_PACKAGING_MATERIAL, REQUEST_REGION } =
  FieldProps;
const {
  FERTILISERS: FERTILIZER_NAME,
  PACKAGING_MATERIALS: PACKAGING_MATERIAL_NAME,
} = Category1TypeFields;

export const GetFormData = (
  props: RawMaterialsLayoutDataProps
): FieldsRowProps => {
  //FieldNames needs to be handled dynamically based on index and type

  const { watch, trigger, resetField } = useFormContext();

  const {
    type,
    index,
    nameOptions,
    bestFitRegionOptions,
    originsLength,
    ingredientsLength,
    remove,
    appendOrigin,
    setIsAddIngredient,
    setIsAddRegion,
    loading,
    fetchData,
    isDisabled,
  } = props;
  const hasMultipleOrigins = originsLength && originsLength > 1;

  const getName = (name: string) => {
    return getFieldName(type, index, name);
  };

  const removeHandler = () => {
    remove?.(index);
    handleLabelChange();
  };

  const handleOpenRequestIngredientModal = () => setIsAddIngredient?.(true);

  const handleOpenRequestRegionModal = () => setIsAddRegion?.(true);
  const typeValue = categoryTypeFieldMap[type];
  const ingredientFieldLabel =
    type === PACKAGING_MATERIALS ? PACKAGING : FERTILISER;
  const originFieldLabel =
    type === PACKAGING_MATERIALS ? PACKAGING_ORIGIN : FERTILISER_ORIGIN;

  const currentLabel =
    type === PACKAGING_MATERIALS ? PACKAGING_MATERIAL_NAME : FERTILIZER_NAME;

  const resetFields = (index: number) => {
    const watchedData = watch(currentLabel)?.[index];
    if (!watchedData) return;

    const fieldsToReset = [
      `${currentLabel}.${index}.activityMappingRegionSpecId`,
      `${currentLabel}.${index}.addressLabel`,
      ...(watchedData.origins ?? [])
        .map((_: unknown, i: number) => [
          `${currentLabel}.${index}.origins.${i}.activityMappingRegionSpecId`,
          `${currentLabel}.${index}.origins.${i}.addressLabel`,
        ])
        .flat(),
    ];

    fieldsToReset.forEach(field => resetField(field, { defaultValue: null }));
    trigger(`${currentLabel}.${index}`);
  };

  const handleLabelChange = (index?: number) => {
    const fieldsToTrigger = watch(currentLabel).map(
      (_: unknown, i: number) => `${currentLabel}.${i}.activityLabel`
    );
    trigger(fieldsToTrigger);
    if (index !== undefined) {
      resetFields(index);
    }
  };

  return {
    data: [
      {
        type: FieldType.ELEMENT,
        additionalElement: (
          <div className={styles.numberContainer}>
            <IndexBadge index={index} />
          </div>
        ),
        span: 1,
      },
      {
        type: FieldType.SELECT,
        fieldProps: {
          name: getName('activityLabel'),
          options: nameOptions,
          label: ingredientFieldLabel,
          showSearch: true,
          placeholderstring: ingredientFieldLabel,
          disabled: isDisabled,
          onChange: () => handleLabelChange(index),
          dropdownRender: (menu: CategoryOptions) => (
            <>
              <div
                className={styles.dropdownCreateButton}
                onClick={handleOpenRequestIngredientModal}>
                <Add />
                {type === PACKAGING_MATERIALS
                  ? REQUEST_PACKAGING_MATERIAL
                  : REQUEST_FERTILISER}
              </div>
              {menu}
            </>
          ),
          onFocus: () =>
            fetchData({
              'input-field':
                ingredientFieldLabel === FERTILISER
                  ? 'farmbox_ag_commodity_fertilizer_name'
                  : 'farmbox_ag_commodity_packaging_material_name',
            }),
        },
        span: 5,
      },
      {
        type: FieldType.LOCATION_MODAL,
        fieldProps: {
          type,
          name: `${typeValue}.${index}.addressLabel`,
          label: originFieldLabel,
          placeholderstring: originFieldLabel,
          disabled: !!hasMultipleOrigins || isDisabled,
          index,
          suffix: <MapSearch />,
          onFocus: () =>
            fetchData({
              'input-field':
                originFieldLabel === FERTILISER_ORIGIN
                  ? 'farmbox_ag_commodity_fertilizer_origin'
                  : 'farmbox_ag_commodity_packaging_material_origin',
            }),
        },
        span: 5,
      },
      {
        type: FieldType.SELECT,
        fieldProps: {
          name: `${typeValue}.${index}.activityMappingRegionSpecId`,
          label: BEST_FIT_REGION,
          placeholderstring: BEST_FIT_REGION,
          filterOption: (inputValue?: string, option?: DefaultOptionType) => {
            const categoryOption = option as CategoryOptions;
            return (
              categoryOption?.label
                ?.toLowerCase()
                .includes(inputValue?.toLowerCase() || '') || false
            );
          },
          options: bestFitRegionOptions,
          disabled: !!hasMultipleOrigins || isDisabled,
          showSearch: true,
          loading: !!loading,
          dropdownRender: (optionsMenu: CategoryOptions) => (
            <>
              <div
                className={styles.dropdownCreateButton}
                onClick={handleOpenRequestRegionModal}>
                <Add /> {REQUEST_REGION}
              </div>
              {optionsMenu}
            </>
          ),
          onFocus: () =>
            fetchData({
              'input-field':
                'farmbox_ag_commodity_select_best_fit_production_region',
            }),
        },
        span: 5,
      },
      {
        type: FieldType.INPUT,
        fieldProps: {
          name: getName('quantity'),
          label: QUANTITY,
          placeholderstring: QUANTITY,
          onKeyDown: allowOnlyNumbersUptoN(4),
          isNullable: true,
          onFocus: () => {
            if (originFieldLabel === FERTILISER_ORIGIN) {
              fetchData({
                'input-field': 'farmbox_ag_commodity_total_quantity_fertilizer',
              });
            }
          },
          disabled: isDisabled,
        },
        span: 2,
      },
      {
        type: FieldType.SELECT,
        fieldProps: {
          name: getName('unit'),
          label: UNIT,
          placeholderstring: UNIT,
          options: commodityQuantityUnits,
          disabled: isDisabled,
        },
        span: 3,
      },
      {
        type: FieldType.ELEMENT,
        additionalElement: (
          <div
            className={clsx(styles.addOrigin, {
              [styles.hideAction]: isDisabled,
            })}
            onClick={appendOrigin}>
            <Tooltip title={ADD_ORIGIN}>
              <Typography.Paragraph>
                <AddIcon />
              </Typography.Paragraph>
            </Tooltip>
          </div>
        ),
        span: 1,
      },
      {
        type: FieldType.ELEMENT,
        additionalElement: ingredientsLength &&
          (type === Category1Types.PACKAGING_MATERIALS ||
            ingredientsLength > 1) && (
            <div
              className={clsx(styles.deleteContainer, {
                [styles.hideAction]: isDisabled,
              })}
              onClick={removeHandler}>
              <DeleteButton />
            </div>
          ),
        span: 1,
      },
    ],
    rowGutter: GUTTER_VALUE,
  };
};
